import React, { FC, useEffect, useState, useCallback } from "react";
import { FeeEarnerCascader, FeeEarner, Rate, Colors } from "@design-system";
import { useQuery } from "@apollo/client";
import { GET_RATES, GET_CURRENCIES } from "../graphql/query";
import { CREATE_RATE } from "../graphql/mutation";
import {
  useAllFeeEarnerLevelsAndFeeEarnersQuery,
  useMutation,
  AllFeeEarnersOption,
  Permission,
  usePermissionCheck,
} from "../hooks";
import { setAutoFreeze } from "immer";
import { CreateRateMutation, CreateRateMutationVariables } from "~/gql/graphql";
import { BaseOptionType } from "antd/lib/cascader";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { Spin, Tooltip } from "antd";
import { GET_MATTER_SPECIFIC_RATES } from "../graphql/query/get-matter-specific-rates";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

//Needed, as we're modifying 'loading' and 'children' props in Immer
setAutoFreeze(false);

type Props = {
  matterId: number;
  currency: { symbol: string; code: string };
  externalMatterId?: number | null;
  clientId?: number | null;
  selectedFeeEarnerLevelId?: number | null;
  selectedFeeEarner?: FeeEarner | null;
  selectedFeeEarnerId?: number | null;
  selectedRateId?: number | null;
  organisationalUnitId?: number;
  loadData?: (selectOptions: BaseOptionType[]) => void;
  onChange: (values: {
    feeEarnerId: number | null;
    selectedRateId: number;
    feeEarnerLevelId: number | null;
  }) => void;
  onClear: () => void;
};

export const FeeEarnerAndRatesCascaderContainer: FC<Props> = ({
  externalMatterId,
  clientId,
  matterId,
  onChange,
  selectedRateId,
  selectedFeeEarner,
  selectedFeeEarnerId,
  organisationalUnitId,
  selectedFeeEarnerLevelId,
  currency,
  onClear,
}) => {
  const [selectedRate, setSelectedRate] = useState<Rate | null>(null);
  const { feeEarnerLevelsAndFeeEarners, setFeeEarnerLevelsAndFeeEarners, allLevels, loading } =
    useAllFeeEarnerLevelsAndFeeEarnersQuery(false, true);

  const findFeeEarnerById = useCallback(
    (feeEarnerId: number) => {
      if (!feeEarnerLevelsAndFeeEarners) return null;

      for (const level of feeEarnerLevelsAndFeeEarners) {
        if (level.children) {
          for (const feeEarner of level.children) {
            if (feeEarner.value === feeEarnerId) {
              return {
                id: feeEarner.value,
                name: feeEarner.label,
              } as FeeEarner;
            }
          }
        }
      }
      return null;
    },
    [feeEarnerLevelsAndFeeEarners]
  );

  const [localSelectedFeeEarner, setLocalSelectedFeeEarner] = useState<
    FeeEarner | null | undefined
  >(selectedFeeEarner);

  useEffect(() => {
    if (selectedFeeEarner) {
      setLocalSelectedFeeEarner(selectedFeeEarner);
    } else if (selectedFeeEarnerId && feeEarnerLevelsAndFeeEarners) {
      const foundFeeEarner = findFeeEarnerById(selectedFeeEarnerId);
      if (foundFeeEarner) {
        setLocalSelectedFeeEarner(foundFeeEarner);
      }
    }
  }, [selectedFeeEarner, selectedFeeEarnerId, feeEarnerLevelsAndFeeEarners, findFeeEarnerById]);

  const [showLoadingRates, setShowLoadingRates] = useState(false);

  const showRateMetaData = usePermissionCheck([Permission.RATE_META_DATA]);

  const { data: currencies } = useQuery(GET_CURRENCIES);
  const isMultiCurrencyInstance = (currencies?.currencies?.length ?? 0) > 1;
  const taxonomy = useTaxonomyLabel();

  const {
    data: ratesData = undefined,
    variables: getRatesVariables,
    loading: loadingRates,
  } = useQuery(GET_RATES, {
    variables: {
      filter: {
        clientId,
        organisationalUnitId,
        feeEarnerId: localSelectedFeeEarner?.id ?? selectedFeeEarnerId,
        externalMatterId,
        feeEarnerLevelId: selectedFeeEarnerLevelId,
        currencyCode: currency.code,
        matterId,
      },
    },
  });

  const { data: matterSpecificRates } = useQuery(GET_MATTER_SPECIFIC_RATES, {
    variables: {
      matterId,
      currencyCode: currency.code,
    },
  });

  // Find the selected rate from combined rates data when rates load or selectedRateId changes
  useEffect(() => {
    if (!selectedRateId) {
      setSelectedRate(null);
      return;
    }

    const allRates = [
      ...(ratesData?.getRates || []),
      ...(matterSpecificRates?.matterSpecificRates || []),
    ];

    const foundRate = allRates.find((rate) => rate.id === selectedRateId);

    if (foundRate) {
      const mappedRate: Rate = {
        id: foundRate.id,
        amount: foundRate.amount,
        description: foundRate.description,
        type: foundRate.type || undefined,
        feeEarnerId: foundRate.feeEarner?.id || null,
        feeEarnerLevelId: foundRate.feeEarnerLevel?.id || null,
        clientId: null,
        deprecated: false,
        currency: {
          id: 0,
          code: currency.code,
          symbol: currency.symbol,
        },
      };

      setSelectedRate(mappedRate);
    } else {
      setSelectedRate(null);
    }
  }, [
    ratesData?.getRates,
    matterSpecificRates?.matterSpecificRates,
    selectedRateId,
    currency.code,
  ]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (loadingRates) {
      timer = setTimeout(() => setShowLoadingRates(true), 750);
      return () => clearTimeout(timer);
    } else {
      setShowLoadingRates(false);
      clearTimeout(timer);
    }
    return () => clearTimeout(timer);
  }, [loadingRates]);

  useEffect(() => {
    setFeeEarnerLevelsAndFeeEarners((draft: any) => {
      draft.map((feLevel: AllFeeEarnersOption<false>) => {
        const feLevelAndOrgUnitRatesRaw = ratesData?.getRates;

        const mattersSpecificRates = matterSpecificRates?.matterSpecificRates;

        const ratesConcat = feLevelAndOrgUnitRatesRaw?.concat([...(mattersSpecificRates ?? [])]);

        feLevel.children = (feLevel?.children || []).map((c) => {
          const feeEarnerLevelAndOrgUnitRates = showLoadingRates
            ? [
                {
                  label: (
                    <Spin
                      style={{
                        padding: 2,
                        marginTop: "45%",
                      }}
                      tip="Loading Rates..."
                    />
                  ),
                  loading: true,
                  disabled: true,
                  value: undefined,
                },
              ]
            : selectedFeeEarnerLevelId
            ? ratesConcat && ratesConcat.length > 0
              ? ratesConcat.map((r: any) => ({
                  label:
                    r.type && showRateMetaData ? (
                      <div>
                        {currency.symbol} {r.amount} ({r.description})
                        <Tooltip title={r.type}>
                          <InfoCircleTwoTone
                            twoToneColor={Colors.Primary}
                            style={{ marginLeft: 5 }}
                          />
                        </Tooltip>
                      </div>
                    ) : (
                      <div>
                        {currency.symbol} {r.amount} ({r.description})
                      </div>
                    ),
                  value: r.id.toString(),
                  loading: true,
                  disabled: false,
                }))
              : loadingRates
              ? []
              : [
                  {
                    value: "no-options",
                    label: (
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        No rates in this currency for this{"\n"}
                        {taxonomy.FeeEarner} / client.
                      </div>
                    ),
                    disabled: true,
                  },
                ]
            : [];

          let currentFeeEarnerRates = feeEarnerLevelAndOrgUnitRates;

          currentFeeEarnerRates = [...feeEarnerLevelAndOrgUnitRates];

          c.children = currentFeeEarnerRates;

          return c;
        });

        return feLevel;
      });
    });
  }, [
    ratesData?.getRates,
    externalMatterId,
    clientId,
    localSelectedFeeEarner?.id,
    selectedFeeEarnerLevelId,
    allLevels,
    loading,
    showLoadingRates,
    matterSpecificRates?.matterSpecificRates,
    currency.symbol,
    showRateMetaData,
  ]);

  const [createRate] = useMutation<CreateRateMutation, CreateRateMutationVariables>(CREATE_RATE);

  const handleChange = useCallback(
    (values: {
      feeEarnerId: number | null;
      selectedRateId: number;
      feeEarnerLevelId: number | null;
    }) => {
      if (values.feeEarnerId) {
        const newFeeEarner = findFeeEarnerById(values.feeEarnerId);
        setLocalSelectedFeeEarner(newFeeEarner);
      } else {
        setLocalSelectedFeeEarner(null);
      }

      onChange(values);
    },
    [onChange, findFeeEarnerById]
  );

  return (
    <FeeEarnerCascader
      matterCurrencySymbol={currency.symbol}
      loading={loading}
      selectedRate={selectedRate}
      selectedFeeEarner={localSelectedFeeEarner}
      selectedFeeEarnerLevelId={selectedFeeEarnerLevelId}
      selectedLevel={allLevels?.find((x) => x.id === selectedFeeEarnerLevelId)}
      isFeeEarnerLevelRate={!localSelectedFeeEarner && !!selectedRate}
      onChange={handleChange}
      onSearch={() => onClear()}
      options={feeEarnerLevelsAndFeeEarners}
      onAddMatterSpecificRate={async (rate) => {
        const matterSpecificRate = await createRate({
          variables: {
            data: {
              ...(externalMatterId && {
                externalMatter: { connect: { id: externalMatterId } },
              }),
              ...(rate?.feeEarnerId && {
                feeEarner: { connect: { id: rate.feeEarnerId } },
              }),
              ...(rate?.feeEarnerLevelId && {
                feeEarnerLevel: { connect: { id: rate.feeEarnerLevelId } },
              }),
              ...(rate?.clientId && {
                client: { connect: { id: rate.clientId } },
              }),
              specificMatterId: matterId,
              amount: rate?.amount || 0,
              description: rate?.description || "Matter specific rate",
              ...(isMultiCurrencyInstance && {
                currency: { connect: { code: currency.code } },
              }),
            },
          },
          update: (cache, createRateResult) => {
            cache.updateQuery(
              {
                query: GET_RATES,
                variables: getRatesVariables,
              },
              (currentData) => {
                if (currentData?.getRates && createRateResult.data?.createRate) {
                  return {
                    getRates: [...currentData?.getRates, createRateResult.data?.createRate],
                  };
                }
              }
            );
          },
        });
        const matterSpecificRateId = matterSpecificRate.data?.createRate?.id;
        if (matterSpecificRateId) {
          onChange({
            feeEarnerId: rate.feeEarnerId ?? null,
            selectedRateId: matterSpecificRateId,
            feeEarnerLevelId: rate.feeEarnerLevelId ?? null,
          });
        }
      }}
    />
  );
};
